import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { companies } from '../data/mockData';
import SEOHead from '../components/SEOHead';

const ImpressumDatenschutz = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const companyList = companyId 
    ? [companies[companyId]]
    : [companies.holding, companies.energiya, companies.mynah, companies.mk];

  useEffect(() => {
    // Scroll to top when page loads or route changes
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, companyId]);

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <SEOHead 
        title="Impressum & Datenschutz | immoriya Projektentwicklung GmbH"
        description="Impressum und Datenschutzerklärung der immoriya Unternehmensgruppe: immoriya Projektentwicklung GmbH, Energiya GmbH, MYNAH GmbH und MK Immobilienverwaltung GmbH."
        canonical="https://immoriya.de/impressum"
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-12">
          Impressum & Datenschutz
        </h1>
        
        {/* Impressum für alle Firmen */}
        {companyList.map((company, idx) => (
          <div key={company.id} className={`bg-white rounded-lg shadow-sm p-8 space-y-6 ${idx < companyList.length - 1 ? 'mb-8' : ''}`}>
            <div className="border-b-2 pb-4 mb-6" style={{ borderColor: company.color }}>
              <h2 className="text-3xl font-bold text-gray-900">
                {company.name}
              </h2>
            </div>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Angaben gemäß § 5 TMG</h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>{company.name}</strong><br />
                {company.address.street}<br />
                {company.address.zip} {company.address.city}<br />
                {company.address.country}
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Vertreten durch</h3>
              <p className="text-gray-700">
                Geschäftsführerin: {company.legal.ceo}
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Kontakt</h3>
              <p className="text-gray-700">
                Telefon: <a href={`tel:${company.contact.phone}`} className="text-blue-600 hover:underline">{company.contact.phone}</a><br />
                E-Mail: <a href={`mailto:${company.contact.email}`} className="text-blue-600 hover:underline">{company.contact.email}</a>
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Registereintrag</h3>
              <p className="text-gray-700">
                Eintragung im Handelsregister<br />
                Registergericht: {company.legal.registerCourt}<br />
                Registernummer: {company.legal.registerNumber}
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Umsatzsteuer-ID</h3>
              <p className="text-gray-700">
                Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
                {company.legal.ustId}
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Steuernummer</h3>
              <p className="text-gray-700">
                {company.legal.steuernummer}
              </p>
            </section>
          </div>
        ))}

        {/* Allgemeine rechtliche Hinweise - nur einmal anzeigen */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6 mt-8">
          <h2 className="text-3xl font-bold text-gray-900 border-b-2 border-gray-300 pb-4 mb-6">
            Rechtliche Hinweise
          </h2>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">EU-Streitschlichtung</h3>
            <p className="text-gray-700 leading-relaxed">
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
              <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                https://ec.europa.eu/consumers/odr/
              </a><br />
              Unsere E-Mail-Adressen finden Sie oben im jeweiligen Impressum.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Verbraucher­streit­beilegung/Universal­schlichtungs­stelle</h3>
            <p className="text-gray-700 leading-relaxed">
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
              Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Haftung für Inhalte</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den 
              allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht 
              verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu 
              forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen 
              Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der 
              Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden 
              Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Haftung für Links</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. 
              Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der 
              verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. 
              Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche 
              Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht 
              zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Urheberrecht</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen 
              Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der 
              Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. 
              Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte 
              Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem 
              auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei 
              Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
            </p>
          </section>
        </div>

        {/* Datenschutzerklärung */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6 mt-8">
          <h2 className="text-3xl font-bold text-gray-900 border-b-2 border-gray-300 pb-4 mb-6">
            Datenschutzerklärung
          </h2>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">1. Datenschutz auf einen Blick</h3>
            <h4 className="text-lg font-semibold text-gray-900 mb-2 mt-4">Allgemeine Hinweise</h4>
            <p className="text-gray-700 leading-relaxed">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
              passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
              persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen 
              Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">2. Datenerfassung auf dieser Website</h3>
            <h4 className="text-lg font-semibold text-gray-900 mb-2 mt-4">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h4>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die Datenverarbeitung auf dieser Website erfolgt durch die jeweiligen Websitebetreiber. 
              Deren Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
            </p>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-2 mt-4">Wie erfassen wir Ihre Daten?</h4>
            <p className="text-gray-700 leading-relaxed mb-4">
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. um 
              Daten handeln, die Sie in ein Kontaktformular eingeben.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere 
              IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder 
              Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">3. Allgemeine Hinweise und Pflichtinformationen</h3>
            <h4 className="text-lg font-semibold text-gray-900 mb-2 mt-4">Datenschutz</h4>
            <p className="text-gray-700 leading-relaxed">
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre 
              personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie 
              dieser Datenschutzerklärung.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">4. Hosting</h3>
            <p className="text-gray-700 leading-relaxed">
              Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, 
              werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, 
              Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe 
              und sonstige Daten, die über eine Website generiert werden, handeln.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">5. Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Datenpakete und richten auf 
              Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung 
              (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">6. Kontaktformular</h3>
            <p className="text-gray-700 leading-relaxed">
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular 
              inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall 
              von Anschlussfragen bei uns gespeichert.
            </p>
          </section>

          <p className="text-sm text-gray-500 mt-8 italic">
            Stand dieser Datenschutzerklärung: Januar 2025
          </p>
        </div>

        <div className="mt-8 text-center">
          <a href="/" className="text-blue-600 hover:underline text-base font-medium">
            ← Zurück zur Startseite
          </a>
        </div>
      </div>
    </div>
  );
};

export default ImpressumDatenschutz;
